import {
  CreateFieldRequest,
  FieldDataType,
  FieldResponse,
  InputFieldDataType,
  NonEmptyArray,
  NumericFieldResponse,
  ResourceFieldResponse,
  SingleSelectFieldResponse,
  UpdateFieldRequest,
} from 'mapistry-shared';
import React, { useCallback, useMemo, useState } from 'react';
import { FieldTypeSelector } from '../../fields/FieldTypeSelector';
import { fieldTypeOptions } from '../fieldTypeOptions';
import {
  BaseFieldWizardModal,
  getError,
  mapFormValuesToBaseRequestDto,
} from './BaseFieldWizardModal';
import { DataTypeFactory } from './DataTypeFactory';

function mapFormValuesToRequestDto(
  values: Partial<FieldResponse>,
  allowVersioning: boolean,
): CreateFieldRequest | UpdateFieldRequest {
  const baseRequestDto = mapFormValuesToBaseRequestDto(values);

  if (allowVersioning) {
    baseRequestDto.isVersioned = !!values.isVersioned;
  }

  switch (values.type) {
    case FieldDataType.NUMERIC: {
      const { units } = values as Partial<NumericFieldResponse>;
      return { ...baseRequestDto, units: units || null };
    }
    case FieldDataType.SINGLE_SELECT: {
      const { options } = values as Partial<SingleSelectFieldResponse>;
      if (!options) throw getError(values);
      return { ...baseRequestDto, options };
    }
    case FieldDataType.RESOURCE: {
      const { resourceTypeId } = values as Partial<ResourceFieldResponse>;
      if (!resourceTypeId) throw getError(values);
      return { ...baseRequestDto, resourceTypeId };
    }
    default:
      return baseRequestDto;
  }
}

const getFieldTypeDisplay = (type: InputFieldDataType) =>
  fieldTypeOptions[type];

interface FieldDataTypeWizardModalProps {
  availableFieldTypes: NonEmptyArray<InputFieldDataType>;
  field?: FieldResponse;
  isRequiredHelpText?: string;
  collectionLabel?: string;
  onClose: () => void;
  onSubmit: (values: CreateFieldRequest | UpdateFieldRequest) => Promise<void>;
  fieldTypeRowCount?: number;
  allowVersioning?: boolean;
}

export function FieldDataTypeWizardModal({
  availableFieldTypes,
  field: inboundField,
  isRequiredHelpText,
  collectionLabel,
  onClose,
  onSubmit: onSubmitProp,
  fieldTypeRowCount = 5,
  allowVersioning = false,
}: FieldDataTypeWizardModalProps) {
  const isEditing = !!inboundField;

  const [field, setField] = useState<Partial<FieldResponse>>(
    inboundField || {},
  );

  const wizardPages = useMemo(
    () => [
      <FieldTypeSelector<InputFieldDataType>
        availableFieldTypes={availableFieldTypes}
        getFieldTypeDisplay={getFieldTypeDisplay}
        rowCount={fieldTypeRowCount}
      />,
      <DataTypeFactory
        dataType={field.type}
        isRequiredHelpText={isRequiredHelpText}
        collectionLabel={collectionLabel}
        isEditing={isEditing}
        allowVersioning={allowVersioning}
      />,
    ],
    [
      availableFieldTypes,
      field.type,
      isEditing,
      isRequiredHelpText,
      collectionLabel,
      fieldTypeRowCount,
      allowVersioning,
    ],
  );

  const isUpdateRequest = (
    x: CreateFieldRequest | UpdateFieldRequest | Partial<FieldResponse>,
  ): x is UpdateFieldRequest => 'id' in x && x.id != null;

  const onNextPage = useCallback(
    async (formValues: Partial<FieldResponse>, pageNumber) => {
      // Reset values if a user goes back and selects a different field type.
      // Note: we are not allowing to change type after a field is created
      if (pageNumber === 0 && !isUpdateRequest(formValues)) {
        formValues.type === field.type
          ? setField({ ...field, ...formValues })
          : setField({ type: formValues.type });
      }
    },
    [field],
  );

  const onSubmit = useCallback(
    async (formValues: Partial<FieldResponse>) => {
      const values = mapFormValuesToRequestDto(formValues, allowVersioning);
      await onSubmitProp(values);
    },
    [onSubmitProp, allowVersioning],
  );

  return (
    <BaseFieldWizardModal
      field={field}
      onClose={onClose}
      onNextPage={onNextPage}
      onPageSubmit={onSubmit}
      showBackButton={!isEditing}
      startPage={isEditing ? 1 : 0}
      wizardPages={wizardPages}
      modalMaxWidth="sm"
    />
  );
}
